import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  getTeachingAttachmentBlob,
  getTeachingById,
  getTeachingPdfBlob,
  getTeachings,
  getTeachingsByInjunctionId,
  getTeachingsByImpositionId,
  getTeachingsByPeriodId,
  openTeachingAttachmentDownload,
  openTeachingDownload,
  updateTeaching,
  type DownloadTeachingAttachmentParams,
  type PaginatedResponse,
  type TeachingDocumentModel,
  type TeachingEvidenceModel,
  type UpdateTeachingParams,
} from "@/api/cle-api";

import {
  mergeEvidenceDetailUpdate,
  reviewThreadAuditTrailQueryKey,
} from "./useReviewThread";

export type { UpdateTeachingParams } from "@/api/cle-api";

export interface UseTeachingsParams {
  userId?: string;
  page?: number;
  pageSize?: number;
  reported?: boolean;
}

export interface UseTeachingParams {
  userId?: string;
  teachingId?: string;
}

export interface UseTeachingsByPeriodParams {
  userId?: string;
  periodId?: string;
}

export interface UseTeachingsByInjunctionParams {
  userId?: string;
  injunctionId?: string;
}

export interface UseTeachingsByImpositionParams {
  userId?: string;
  impositionId?: string;
}

export interface DownloadTeachingParams {
  userId: string;
  teachingId: string;
}

export const teachingsQueryKey = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseTeachingsParams) =>
  ["cle-api", "users", userId, "teachings", page, pageSize, reported] as const;

export const teachingsByPeriodQueryKey = ({
  userId,
  periodId,
}: UseTeachingsByPeriodParams) =>
  ["cle-api", "users", userId, "teachings", "by-period", periodId] as const;

export const teachingsByInjunctionQueryKey = ({
  userId,
  injunctionId,
}: UseTeachingsByInjunctionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "teachings",
    "by-injunction",
    injunctionId,
  ] as const;

export const teachingsByImpositionQueryKey = ({
  userId,
  impositionId,
}: UseTeachingsByImpositionParams) =>
  [
    "cle-api",
    "users",
    userId,
    "teachings",
    "by-imposition",
    impositionId,
  ] as const;

export const teachingQueryKey = ({ userId, teachingId }: UseTeachingParams) =>
  ["cle-api", "users", userId, "teachings", teachingId] as const;

export const teachingPdfQueryKey = ({
  userId,
  teachingId,
}: UseTeachingParams) =>
  ["cle-api", "users", userId, "pdf", "teachings", teachingId] as const;

export const teachingAttachmentPdfQueryKey = ({
  userId,
  teachingId,
  attachmentId,
}: DownloadTeachingAttachmentParams) =>
  [
    "cle-api",
    "users",
    userId,
    "pdf",
    "teachings",
    teachingId,
    "attachments",
    attachmentId,
  ] as const;

export const useTeachings = ({
  userId,
  page = 1,
  pageSize = 20,
  reported,
}: UseTeachingsParams): UseQueryResult<
  PaginatedResponse<TeachingDocumentModel>,
  Error
> =>
  useQuery({
    queryKey: teachingsQueryKey({ userId, page, pageSize, reported }),
    queryFn: () =>
      getTeachings({
        userId: userId ?? "",
        page,
        pageSize,
        reported,
      }),
    enabled: !!userId,
  });

export const useTeachingsByPeriod = ({
  userId,
  periodId,
}: UseTeachingsByPeriodParams): UseQueryResult<
  TeachingDocumentModel[],
  Error
> =>
  useQuery({
    queryKey: teachingsByPeriodQueryKey({ userId, periodId }),
    queryFn: () =>
      getTeachingsByPeriodId({
        userId: userId ?? "",
        periodId: periodId ?? "",
      }),
    enabled: !!userId && !!periodId,
  });

export const useTeachingsByInjunction = ({
  userId,
  injunctionId,
}: UseTeachingsByInjunctionParams): UseQueryResult<
  TeachingDocumentModel[],
  Error
> =>
  useQuery({
    queryKey: teachingsByInjunctionQueryKey({ userId, injunctionId }),
    queryFn: () =>
      getTeachingsByInjunctionId({
        userId: userId ?? "",
        injunctionId: injunctionId ?? "",
      }),
    enabled: !!userId && !!injunctionId,
  });

export const useTeachingsByImposition = ({
  userId,
  impositionId,
}: UseTeachingsByImpositionParams): UseQueryResult<
  TeachingDocumentModel[],
  Error
> =>
  useQuery({
    queryKey: teachingsByImpositionQueryKey({ userId, impositionId }),
    queryFn: () =>
      getTeachingsByImpositionId({
        userId: userId ?? "",
        impositionId: impositionId ?? "",
      }),
    enabled: !!userId && !!impositionId,
  });

export const useTeaching = ({
  userId,
  teachingId,
}: UseTeachingParams): UseQueryResult<TeachingEvidenceModel, Error> =>
  useQuery({
    queryKey: teachingQueryKey({ userId, teachingId }),
    queryFn: () =>
      getTeachingById({
        userId: userId ?? "",
        teachingId: teachingId ?? "",
      }),
    enabled: !!userId && !!teachingId,
  });

export const useDownloadTeaching = (): UseMutationResult<
  void,
  Error,
  DownloadTeachingParams
> =>
  useMutation({
    mutationFn: openTeachingDownload,
  });

export const useTeachingPdf = ({
  userId,
  teachingId,
}: UseTeachingParams): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: teachingPdfQueryKey({ userId, teachingId }),
    queryFn: () =>
      getTeachingPdfBlob({
        userId: userId ?? "",
        teachingId: teachingId ?? "",
      }),
    enabled: !!userId && !!teachingId,
  });

export const useUpdateTeaching = (): UseMutationResult<
  TeachingEvidenceModel,
  Error,
  UpdateTeachingParams
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTeaching,
    onSuccess: (data, { teachingId, userId }) => {
      queryClient.setQueryData<TeachingEvidenceModel>(
        teachingQueryKey({ userId, teachingId }),
        (old) => mergeEvidenceDetailUpdate(old, data),
      );
      void queryClient.invalidateQueries({
        queryKey: reviewThreadAuditTrailQueryKey(teachingId),
      });
      void queryClient.invalidateQueries({
        queryKey: ["cle-api", "users", userId, "teachings"],
      });
    },
  });
};

/* --- Supplerende dokumentation (bilag), ADV-359 ------------------------- */

export const useDownloadTeachingAttachment = (): UseMutationResult<
  void,
  Error,
  DownloadTeachingAttachmentParams
> =>
  useMutation({
    mutationFn: openTeachingAttachmentDownload,
  });

/** Bilaget som blob, til forhaandsvisning i en dialog. Som for kursusbeviser
 * kun aktiveret, naar dialogen er aaben. */
export const useTeachingAttachmentPdf = (
  { userId, teachingId, attachmentId }: DownloadTeachingAttachmentParams,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<Blob, Error> =>
  useQuery({
    queryKey: teachingAttachmentPdfQueryKey({
      userId,
      teachingId,
      attachmentId,
    }),
    queryFn: () =>
      getTeachingAttachmentBlob({ userId, teachingId, attachmentId }),
    enabled: enabled && !!userId && !!teachingId && !!attachmentId,
  });
